import React, { useMemo, useState } from "react";

import { Autocomplete, Popover, Stack, TextField } from "@mui/material";
import { AddValueButton } from "components/Deal-workspace/Workspace-metadata-components/Cells/AddValueCell";
import { EditableCell } from "components/Deal-workspace/Workspace-metadata-components/Cells/EditableCell";
import { PromptFooter } from "components/DealGroupedTable/PromptFooter";
import { useDealActions } from "store";
import { DealAssetSimple, IAssetClass } from "store/types";
import { toastify } from "utils/tyostify";

import { ValueCell } from "./DealTableCells";

interface Props {
  mode: "asset" | "subAsset";
  value: DealAssetSimple | null;
  dealId: number;
  parentAsset?: DealAssetSimple | null;
  assetClasses: IAssetClass[];
  /** Only admins / team members may open the editor; others see read-only values. */
  canEdit: boolean;
  /** New value after a successful patch (null = sub-asset cleared), so the table state updates in place. */
  onSuccess: (updated: DealAssetSimple | null) => void;
}

const assetLabel = (asset: IAssetClass): string => asset.account_alias || asset.presentation_name;

export const AssetClassCell: React.FC<Props> = ({
  mode,
  value,
  dealId,
  parentAsset,
  assetClasses,
  canEdit,
  onSuccess
}) => {
  const { updateDealAction } = useDealActions();

  const currentId = value?.id ?? null;
  const parentAssetId = parentAsset?.id;
  const displayName = value?.name ?? null;

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(currentId);

  const isSub = mode === "subAsset";

  const handleOpen = (e: React.MouseEvent<HTMLDivElement>) => {
    setSelectedId(currentId);
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const options = useMemo<IAssetClass[]>(() => {
    if (!isSub) return assetClasses;
    const parent =
      assetClasses.find((asset) => asset.id === parentAssetId) ??
      assetClasses.find((asset) => asset.sub_asset_class.some((sub) => sub.id === currentId));
    return parent?.sub_asset_class ?? [];
  }, [isSub, assetClasses, parentAssetId, currentId]);

  const selected = options.find((option) => option.id === selectedId);

  const handleApply = async () => {
    if (selectedId === currentId) {
      handleClose();
      return;
    }

    if (!isSub && selectedId == null) {
      handleClose();
      return;
    }
    const option = selectedId != null ? options.find((item) => item.id === selectedId) : undefined;
    try {
      // skipDispatch: the row is patched locally, so we avoid touching the current-deal store slice.
      await updateDealAction(
        { dealId, ...(isSub ? { sub_asset_class_id: selectedId } : { asset_class_id: selectedId as number }) },
        { skipDispatch: true }
      );
      handleClose();
      onSuccess(option ? { id: option.id, name: assetLabel(option) } : null);
    } catch (error) {
      // Keep the popover open so the user can retry.
      toastify({ message: (error as Error)?.message || "Failed to update", type: "error" });
    }
  };

  // Read-only for non-editors: show the value (no pencil / no "+").
  if (!canEdit) return <ValueCell value={displayName} variant="text" />;

  return (
    <>
      {displayName !== null ? (
        <EditableCell value={displayName} onClick={handleOpen} color="#26a6ae" />
      ) : (
        <AddValueButton onClick={handleOpen} />
      )}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Stack sx={{ width: 320, p: "16px", gap: "16px" }}>
          <Autocomplete
            options={options}
            value={selected ?? null}
            onChange={(_, option) => setSelectedId(option?.id ?? null)}
            getOptionLabel={assetLabel}
            isOptionEqualToValue={(option, val) => option.id === val.id}
            // Asset class is required (not clearable); a sub-asset can be cleared → sub_asset_class_id: null.
            disableClearable={!isSub}
            size="small"
            renderInput={(params) => <TextField {...params} label={isSub ? "Sub Asset Class" : "Asset Class"} />}
          />
          <PromptFooter onCancel={handleClose} onApply={handleApply} />
        </Stack>
      </Popover>
    </>
  );
};
